import { RouteRecordRaw } from 'vue-router'
import { Component } from 'vue'
import { constantRoutes, defaultRoutes } from './constantRoutes'
import { Menu } from '@/types/Menu'

/**
 * 收集所有子模块下的 routes.ts 配置（static 模式使用，不依赖后台权限）
 * 路径约定：views 下的 routes.ts 默认导出 RouteRecordRaw[]（相对 Layout 的子路由）
 */
const modules = import.meta.glob(['../views/**/routes.ts'], { eager: true })

// 一级菜单显示顺序由各模块 routes.ts 的顶层 meta.order 决定
// 未设置 order 的模块排在其后，保持原有（加载）顺序
const orderOf = (route: RouteRecordRaw): number => {
  const order = (route.meta as { order?: number } | undefined)?.order
  return typeof order === 'number' ? order : Number.MAX_SAFE_INTEGER
}

export const moduleRoutes: RouteRecordRaw[] = []

/**
 * 按 name（缺省按 path）合并同名路由：将分散在多个模块 routes.ts 中、
 * 指向同一父级（如 `system`）的子路由合并到同一个父级下。
 * 父级自身属性（component/redirect/meta）以最先定义且非空的为准，
 * 子路由则累加合并。
 */
function mergeRoutesByName(routes: RouteRecordRaw[]): RouteRecordRaw[] {
  const map = new Map<string, RouteRecordRaw>()
  for (const route of routes) {
    const key = (route.name as string) || (route.path as string)
    if (!map.has(key)) {
      map.set(key, {
        ...route,
        children: route.children ? mergeRoutesByName(route.children as RouteRecordRaw[]) : undefined
      })
    } else {
      const existing = map.get(key)!
      const existingChildren = (existing.children as RouteRecordRaw[]) || []
      const newChildren = (route.children as RouteRecordRaw[]) || []
      existing.children = mergeRoutesByName([...existingChildren, ...newChildren])
      existing.component = existing.component || route.component
      existing.redirect = existing.redirect || route.redirect
      existing.meta = { ...(route.meta || {}), ...(existing.meta || {}) }
    }
  }
  return Array.from(map.values())
}

const rawModuleRoutes: RouteRecordRaw[] = []
Object.keys(modules).forEach((key) => {
  const mod = (modules[key] as { default?: RouteRecordRaw[] }).default
  if (Array.isArray(mod)) {
    rawModuleRoutes.push(...mod)
  }
})

Object.assign(moduleRoutes, mergeRoutesByName(rawModuleRoutes))

// 按 meta.order 稳定排序（递归至子路由）：已设置的按升序，未设置的保持原顺序排在后面
function sortRoutesByOrder(routes: RouteRecordRaw[]): void {
  routes.sort((a, b) => orderOf(a) - orderOf(b))
  routes.forEach((r) => {
    if (r.children?.length) sortRoutesByOrder(r.children as RouteRecordRaw[])
  })
}
sortRoutesByOrder(moduleRoutes)

/**
 * 菜单同步路由：两种模式都注入，方便后续一键同步菜单到后台
 * hidden: true 表示不放入左侧菜单，但保留路由可访问 /menu-sync
 */
export const menuSyncRoute: RouteRecordRaw = {
  path: 'menu-sync',
  name: 'menu-sync',
  component: () => import('@/views/ZDev/MenuSync/index.vue'),
  meta: {
    title: '菜单同步',
    icon: 'ep:refresh',
    hidden: true
  }
}

/** 移动端权限一键同步路由 */
export const mobileMenuSyncRoute: RouteRecordRaw = {
  path: 'menu-mobile-sync',
  name: 'menu-mobile-sync',
  component: () => import('@/views/ZDev/MenuSync/mobile.vue'),
  meta: {
    title: '移动端权限同步',
    icon: 'ep:mobile',
    hidden: true
  }
}

function resolvePath(path: string, parent: string): string {
  if (!path) return parent || '/'
  if (path.startsWith('/')) return path
  if (!parent) return '/' + path.replace(/^\/+/, '')
  return (parent + '/' + path).replace(/\/+/g, '/').replace(/\/$/, '')
}

function toMenu(route: RouteRecordRaw, parentPath = ''): Menu {
  const fullPath = resolvePath(route.path as string, parentPath)
  const menu: Menu = {
    path: fullPath,
    name: (route.name as string) || fullPath,
    component: route.component as unknown as Component | undefined,
    redirect: route.redirect as unknown as string | undefined,
    meta: (route.meta as unknown as Menu['meta']) || {}
  }
  if (route.children?.length) {
    menu.children = (route.children as RouteRecordRaw[]).map((c) => toMenu(c, fullPath))
  }
  return menu
}

/**
 * 构建静态菜单树（左侧一级菜单 = Layout 的子路由）
 * @param includeModules 是否纳入子模块 routes.ts（静态模式 true，动态模式 false）
 */
export function buildStaticMenuTree(includeModules: boolean): Menu[] {
  const tree: Menu[] = []

  // 工作台等前端写死的 Layout 子路由：两种模式都进菜单
  const layoutRoute = constantRoutes.find((r) => r.path === '/')
  if (layoutRoute?.children?.length) {
    layoutRoute.children.forEach((child) => {
      tree.push(toMenu(child as RouteRecordRaw, ''))
    })
  }

  // 静态模式：纳入子模块 routes.ts、菜单同步
  if (includeModules) {
    moduleRoutes.forEach((r) => tree.push(toMenu(r, '')))
    tree.push(toMenu(menuSyncRoute, ''))
    tree.push(toMenu(mobileMenuSyncRoute, ''))
  }

  // 独立页面（/bi 等），排除 /login（登录页不进菜单）
  // 动态模式下仅保留这部分前端菜单，工作台/子模块由后台权限驱动
  const standalone = constantRoutes
    .concat(defaultRoutes)
    .filter((r) => r.path !== '/' && r.path !== '/login')
  standalone.forEach((r) => tree.push(toMenu(r, '')))

  return tree
}
